import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import { user } from 'src/StateObserver';
import * as Social from 'src/replicant/social';
import { Feature } from 'src/replicant/analytics';
import { Avatar } from 'src/game/components/shared/Avatar';
import { GiftsSocialFriend } from '../helpers';

export type GiftsListItem = GiftsSocialFriend & { type: 'friend' | 'invite' };

const WIDTH = 510;
const HEIGHT = 95;
const LABEL_WIDTH = 225;

type Opts = {
  superview: View;
  createActionButton: (item: GiftsListItem) => View;
  getActivityText: (item: GiftsListItem) => string;
  onInvite: () => Promise<void>;
};

/**
 * One recycled row of the gifts list: a friend with its action button, or the
 * trailing invite row. `Scroll` keeps only the visible rows alive and calls
 * `setData` as they scroll into view.
 */
export class GiftsFriendRow extends View {
  private friendContent: View;
  private inviteContent: View;
  private avatar: Avatar;
  private friendName: LangBitmapFontTextView;
  private activityText: LangBitmapFontTextView;
  private button?: View;
  private item?: GiftsListItem;

  constructor(private opts: Opts) {
    super({
      tag: 'item',
      superview: opts.superview,
      width: WIDTH,
      height: HEIGHT,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: this,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      scaleMethod: '9slice',
      width: WIDTH,
      height: HEIGHT,
      x: (opts.superview.style.width - WIDTH) * 0.5,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    this.friendContent = new View({
      superview: this,
      width: WIDTH,
      height: HEIGHT,
    });

    this.avatar = new Avatar({
      superview: this.friendContent,
      x: 32,
      y: 10,
      width: 68,
      height: 68,
      centerOnOrigin: false,
    });

    this.friendName = new LangBitmapFontTextView({
      tag: 'friend-name',
      superview: this.friendContent,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 108,
      y: 10,
      size: 28,
      width: LABEL_WIDTH,
      height: 35,
      font: bitmapFonts('Body'),
      localeText: () => this.item?.name ?? '',
      letterSpacing: -2,
    });

    this.activityText = new LangBitmapFontTextView({
      tag: 'activity-text',
      superview: this.friendContent,
      align: 'left',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: false,
      x: 111,
      y: 44,
      size: 16,
      width: LABEL_WIDTH,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () => (this.item ? opts.getActivityText(this.item) : ''),
      letterSpacing: -2,
    });

    this.inviteContent = this.createInviteContent();
  }

  public setData(item: GiftsListItem) {
    this.item = item;

    const isInvite = item.type === 'invite';
    this.inviteContent.style.visible = isInvite;
    this.friendContent.style.visible = !isInvite;

    if (isInvite) return;

    void this.avatar.updateImage(item.photo ?? '');

    // Re-assign so the labels re-render with the new item
    this.friendName.localeText = () => item.name ?? '';
    this.activityText.localeText = () => this.opts.getActivityText(item);

    // The button look and handler depend on the item state, so rebuild it
    this.button?.removeFromSuperview();
    this.button = this.opts.createActionButton(item);
    this.friendContent.addSubview(this.button);
  }

  private createInviteContent() {
    const view = new View({
      superview: this,
      width: WIDTH,
      height: HEIGHT,
      visible: false,
    });

    new ImageView({
      tag: 'invite-icon',
      superview: view,
      image: 'assets/ui/friends/friend_referral_icon.png',
      width: 70,
      height: 56,
      x: 30,
      y: 13,
      scale: 1.1,
    });

    new LangBitmapFontTextView({
      tag: 'invite-title',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#7192AB',
      wordWrap: false,
      x: 114,
      y: 20,
      size: 19,
      width: 245,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () => i18n('gifts.popupGiftsList.inviteTitle_ingame'),
      letterSpacing: -2,
    });

    new LangBitmapFontTextView({
      tag: 'description',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: false,
      x: 114,
      y: 46,
      size: 13,
      width: LABEL_WIDTH,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () =>
        i18n('gifts.popupGiftsList.inviteDescription', {
          reward: Social.getters.getInviteReward(
            user(),
            Feature.INVITE.INVITE_GIFTS,
          ).value,
        }),
      letterSpacing: -2,
    });

    new ButtonScaleViewWithText({
      tag: 'invite-button',
      superview: view,
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: 0,
      labelOffsetY: -2,
      x: WIDTH - 130,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.inviteBtn'),
      onClick: () => this.opts.onInvite(),
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-invite-button',
      },
    });

    return view;
  }
}
